import type { Rates } from "./types";

/**
 * Francis Dunleavy email "4 Quarter Time Share N426DH" (Dec 16, 2025) vs
 * the twelve Q4 2025 `[Time share]` legs in `web/src/lib/fixtures/dunleavyQ4_2025_timeshare.csv`
 * (snapshotted from the logbook; Vitest uses this fixture, not the full `logbook_converted_latest.csv`).
 * Single source for {@link fourQuarterTimeshareN426dh.test.ts} and the Comparison tab UI.
 */
export const DUNLEAVY_Q4_COMPARISON_RATES: Rates = {
  domesticSegmentUsdPerPassengerPerSegment: 4.8,
  internationalFacilityUsdPerPerson: 21.1,
  airTransportationPercentage: 0.075,
  alaskaHawaiiDomesticDepartureUsdPerPerson: 11.7,
  fuelLbsPerGallon: 6.5,
};

/**
 * IRS No. 26 domestic segment rate for **calendar year 2025** flights (indexed; matches
 * `form720/config_calendar_year_2025.json` and Form 720 instructions). Comparison table
 * shows this beside the **$4.80** email-shorthand column.
 */
export const DUNLEAVY_CY2025_DOMESTIC_SEGMENT_USD = 5.2;

/**
 * IRS indexed **calendar-year 2025** passenger air amounts (Rev. Proc. 2024-40).
 * Same charter % and fuel density as {@link DUNLEAVY_Q4_COMPARISON_RATES} for apples-to-apples
 * comparison with the email-shorthand column.
 */
export const DUNLEAVY_CY2025_IRS_PASSENGER_RATES: Rates = {
  domesticSegmentUsdPerPassengerPerSegment: 5.2,
  internationalFacilityUsdPerPerson: 22.9,
  airTransportationPercentage: 0.075,
  alaskaHawaiiDomesticDepartureUsdPerPerson: 11.4,
  fuelLbsPerGallon: 6.5,
};

/**
 * Form 720 **default** passenger lines (matches the Form 720 tab before user edits).
 * Comparison column 3 — built-in worksheet-default benchmark (not user-editable).
 */
export const DUNLEAVY_WORKSHEET_DEFAULT_PASSENGER_RATES: Rates = {
  domesticSegmentUsdPerPassengerPerSegment: 5.3,
  internationalFacilityUsdPerPerson: 23.4,
  airTransportationPercentage: 0.075,
  alaskaHawaiiDomesticDepartureUsdPerPerson: 11.7,
  fuelLbsPerGallon: 6.5,
};

/** Use {@link DUNLEAVY_WORKSHEET_DEFAULT_PASSENGER_RATES} domestic for row-by-row “alt” domestic column. */
export const DUNLEAVY_WORKSHEET_DEFAULT_DOMESTIC_SEGMENT_USD =
  DUNLEAVY_WORKSHEET_DEFAULT_PASSENGER_RATES.domesticSegmentUsdPerPassengerPerSegment;

/**
 * Passenger lines from <code>passengerTemplate</code>; fuel density and charter % from the live worksheet (Form 720 tab).
 */
export function mergeWorksheetFuelCharterOnly(
  worksheet: Rates,
  passengerTemplate: Rates
): Rates {
  return {
    ...passengerTemplate,
    fuelLbsPerGallon: worksheet.fuelLbsPerGallon,
    airTransportationPercentage: worksheet.airTransportationPercentage,
  };
}

/** Email-era passenger lines for Comparison column 2 (same amounts as column 1 excerpt; fuel/charter still from worksheet). */
export function resolveEmailBenchmarkPassengerRates(): Pick<
  Rates,
  | "domesticSegmentUsdPerPassengerPerSegment"
  | "internationalFacilityUsdPerPerson"
  | "alaskaHawaiiDomesticDepartureUsdPerPerson"
> {
  const b = DUNLEAVY_Q4_COMPARISON_RATES;
  return {
    domesticSegmentUsdPerPassengerPerSegment: b.domesticSegmentUsdPerPassengerPerSegment,
    internationalFacilityUsdPerPerson: b.internationalFacilityUsdPerPerson,
    alaskaHawaiiDomesticDepartureUsdPerPerson: b.alaskaHawaiiDomesticDepartureUsdPerPerson,
  };
}

/** Column 2 totals: Form 720 fuel + charter %, passenger lines from {@link resolveEmailBenchmarkPassengerRates}. */
export function mergeWorksheetWithEmailBenchmarkRates(worksheet: Rates): Rates {
  const e = resolveEmailBenchmarkPassengerRates();
  return {
    ...worksheet,
    domesticSegmentUsdPerPassengerPerSegment: e.domesticSegmentUsdPerPassengerPerSegment,
    internationalFacilityUsdPerPerson: e.internationalFacilityUsdPerPerson,
    alaskaHawaiiDomesticDepartureUsdPerPerson: e.alaskaHawaiiDomesticDepartureUsdPerPerson,
  };
}

/** Column 3 totals: Form 720 fuel + charter %, built-in worksheet-default passenger lines ($5.30 / $23.40 / $11.70). */
export function mergeWorksheetWithWorksheetDefaultBenchmarkRates(worksheet: Rates): Rates {
  const d = DUNLEAVY_WORKSHEET_DEFAULT_PASSENGER_RATES;
  return {
    ...worksheet,
    domesticSegmentUsdPerPassengerPerSegment: d.domesticSegmentUsdPerPassengerPerSegment,
    internationalFacilityUsdPerPerson: d.internationalFacilityUsdPerPerson,
    alaskaHawaiiDomesticDepartureUsdPerPerson: d.alaskaHawaiiDomesticDepartureUsdPerPerson,
  };
}

/** @deprecated Use {@link mergeWorksheetWithWorksheetDefaultBenchmarkRates} (defaults are now 5.30 worksheet, not 5.20 IRS). */
export const mergeWorksheetWithIrsCyBenchmarkRates = mergeWorksheetWithWorksheetDefaultBenchmarkRates;

export function resolveWorksheetDefaultDomesticSegmentUsd(): number {
  return DUNLEAVY_WORKSHEET_DEFAULT_DOMESTIC_SEGMENT_USD;
}

/** @deprecated Use {@link resolveWorksheetDefaultDomesticSegmentUsd}. */
export const resolveIrsCyDomesticSegmentUsd = resolveWorksheetDefaultDomesticSegmentUsd;

export type Q4ComparisonTotals = {
  legCount: number;
  /** IRS No. 26 domestic segment */
  domesticSegment: number;
  /** IRS No. 27 international facility */
  internationalFacility: number;
  combinedAirExcise: number;
  /** Utah Gas reimbursement — 2× fuel (agreement §2.3(j) style) */
  twoXFuel: number;
  /** Optional Form 720 model: 7.5% × 2× fuel base */
  optionalPersonalPctTax: number;
  grandTotal: number;
};

/** Synthetic 12 legs built so the **email arithmetic** matches exactly. */
export const DUNLEAVY_SYNTHETIC_Q4_EXPECTED: Q4ComparisonTotals = {
  legCount: 12,
  domesticSegment: 105.6,
  internationalFacility: 147.7,
  combinedAirExcise: 253.3,
  twoXFuel: 24398,
  optionalPersonalPctTax: 1829.85,
  grandTotal: 26481.15,
};

/** Worksheet output on the 12-leg Q4 fixture rows (passenger/segment + fuel from log). */
export const DUNLEAVY_LOGBOOK_Q4_EXPECTED: Q4ComparisonTotals = {
  legCount: 12,
  /** Domestic segment only where neither AK departure nor international facility tax applies on that leg. */
  domesticSegment: 105.6,
  internationalFacility: 147.7,
  combinedAirExcise: 253.3,
  twoXFuel: 24399.12,
  optionalPersonalPctTax: 1829.95,
  grandTotal: 26482.37,
};

export function formatComparisonUsd(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Manual 2026 Q1 arithmetic (Utah Gas / timeshare package) for Comparison tab.
 * Rates: 7.5% charter, $11.70 AK/HI departure, $5.30 domestic segment, $23.40 intl facility.
 */
export type Q1_2026ComparisonReference = {
  /** Calendar scope label */
  periodLabel: string;
  twoXFuel: number;
  charterPctTax: number;
  alaskaHawaiiDepartureTax: number;
  domesticSegmentTax: number;
  internationalFacilityTax: number;
  /** Sum of charter + AK + dom + intl passenger/air lines (no 2× fuel). */
  totalAirExciseTax: number;
  /** 2× fuel + total air excise (no optional % on 2× fuel in this reference). */
  owedUtahGasCorp: number;
  notes: {
    charter: string;
    akHi: string;
    domestic: string;
    international: string;
  };
};

export const Q1_2026_COMPARISON_REFERENCE: Q1_2026ComparisonReference = {
  periodLabel: "2026 Q1",
  twoXFuel: 21233.92,
  charterPctTax: 1592.54,
  alaskaHawaiiDepartureTax: 105.3,
  domesticSegmentTax: 63.6,
  internationalFacilityTax: 234,
  totalAirExciseTax: 1995.44,
  owedUtahGasCorp: 23229.36,
  notes: {
    charter:
      "7.5% on transportation base (manual package used charter $); worksheet may show the same dollars under optional % on 2× fuel when charter $ is blank.",
    akHi:
      "3 pax × 3 legs × $11.70 (manual shorthand). Worksheet: tax on departures from PA…/PH… to another U.S. point only; use akhi_pax to match legs that arrive in AK/HI without a PA/PH departure on that row.",
    domestic:
      "6 pax × 2 legs × $5.30 (manual shorthand). Worksheet: domestic segment only on legs with neither AK/HI departure nor international facility tax.",
    international:
      "5 pax × 2 legs × $23.40 (manual shorthand). Worksheet: intl_pax on U.S.↔non-U.S.; not stacked with domestic segment on the same leg.",
  },
};
